import * as React from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { FormControl, Grid, Box, InputAdornment, Avatar } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { ContractorPackageLineItem } from "@interfaces/contractorPackage";
import { NumericFormatCustom } from "@stories/atoms/NumericFormatCustom/NumericFormatCustom";
import { useState } from "react";
import { useOrganisation } from "@hooks/crud/useOrganisation";
import { OrganisationFinder } from "../OrganisationFinder/OrganisationFinder";
import { OrganisationLineItem } from "@interfaces/organisation";
import { StyledTextField } from "@stories/atoms/StyledTextField/StyledTextField";
import { StyledButton } from "@stories/atoms/StyledButton/StyledButton";
import { ProjectLineItem } from "@interfaces/project";

interface PackageDetailsFormProps {
  project: ProjectLineItem;
  contractorPackage: ContractorPackageLineItem;
  handleSubmit: (
    contractorPackage: ContractorPackageLineItem,
    contractor: OrganisationLineItem
  ) => void;
}

export default function PackageDetailsForm({
  project,
  contractorPackage,
  handleSubmit,
}: PackageDetailsFormProps) {
  const [contractor, setContractor] = React.useState<
    OrganisationLineItem | undefined
  >(undefined);

  const { searchOrganisations } = useOrganisation();

  const [offsetPackageValue, setOffsetPackageValue] = useState(
    contractorPackage.totalPackageValue + contractorPackage.variations
  );

  const validationSchema = yup.object({
    contractor: yup.string().required("Contractor name is required"),
    variations: yup
      .number()
      .required("Package value is required")
      .test(
        "Total package value cannot be negative",
        "Total package value cannot be negative",
        function (item) {
          return this.parent.totalPackageValue + item >= 0;
        }
      ),
    totalPackageValue: yup
      .number()
      .test(
        "Total package value cannot be negative",
        "Total package value cannot be negative",
        function (item) {
          return this.parent.variations + item >= 0;
        }
      ),
    startDate: yup.date().required("Start date due date is required"),
    endDate: yup
      .date()
      .required("End date due date is required")
      .test(
        "After start date",
        "Completion date must be after start date",
        function (item) {
          if (this.parent.startDate) {
            return this.parent.startDate < item;
          }
          return true;
        }
      ),
  });

  const displayName = contractorPackage.contractorName || "";

  const formik = useFormik({
    initialValues: {
      contractor: "",
      variations: contractorPackage.variations,
      totalPackageValue: contractorPackage.totalPackageValue,
      startDate: dayjs(contractorPackage.startDate),
      endDate: dayjs(contractorPackage.endDate),
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      if (contractor) {
        handleSubmit(
          {
            ...contractorPackage,
            variations: values.variations,
            totalPackageValue: values.totalPackageValue,
            startDate: values.startDate.toDate(),
            endDate: values.endDate.toDate(),
          },
          contractor
        );
      }
    },
  });

  const handleVariationsUpdate = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseFloat(event.target.value);
    const totalPackageValue = formik.values.totalPackageValue + value;
    setOffsetPackageValue(totalPackageValue);
    formik.setFieldValue("variations", value);
  };

  const handlePackageValueUpdate = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseFloat(event.target.value);
    const totalPackageValue = formik.values.variations + value;
    setOffsetPackageValue(totalPackageValue);
    formik.setFieldValue("totalPackageValue", value);
  };
  const onOrgChange = (organisation?: OrganisationLineItem) => {
    formik.setFieldValue("contractor", organisation?.id || "");
    setContractor(organisation);
  };

  return (
    <Box sx={{ padding: "24px !important" }}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={6} md={6} lg={6}>
            {!contractorPackage.contractorId ? (
              <OrganisationFinder
                id="contractor"
                name="contractor"
                label="Search Subcontractor"
                disabled={formik.isSubmitting}
                handleSearch={searchOrganisations}
                onChange={onOrgChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.contractor && Boolean(formik.errors.contractor)
                }
                helperText={
                  formik.touched.contractor ? formik.errors.contractor : ""
                }
              />
            ) : (
              <FormControl fullWidth>
                <StyledTextField
                  fullWidth
                  margin="dense"
                  value={displayName}
                  disabled={true}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Avatar src={contractorPackage.contractorLogo}>
                          {contractorPackage.contractorName.toUpperCase()}
                        </Avatar>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            )}
          </Grid>
          <Grid item xs={6} sm={6}>
            <FormControl fullWidth>
              <StyledTextField
                id="totalPackageValue"
                name="totalPackageValue"
                label="Package Value (ex. GST)"
                fullWidth
                margin="dense"
                onChange={handlePackageValueUpdate}
                onBlur={formik.handleBlur}
                value={formik.values.totalPackageValue}
                error={
                  formik.touched.totalPackageValue &&
                  Boolean(formik.errors.totalPackageValue)
                }
                helperText={
                  formik.touched.totalPackageValue
                    ? formik.errors.totalPackageValue
                    : ""
                }
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={6}>
            <FormControl fullWidth>
              <StyledTextField
                id="variations"
                name="variations"
                label="Variations (ex. GST)"
                fullWidth
                margin="dense"
                onChange={handleVariationsUpdate}
                onBlur={formik.handleBlur}
                value={formik.values.variations}
                error={
                  formik.touched.variations && Boolean(formik.errors.variations)
                }
                helperText={
                  formik.touched.variations ? formik.errors.variations : ""
                }
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6} md={6} sm={6}>
            <FormControl fullWidth>
              <StyledTextField
                id="offsetPackageValue"
                name="offsetPackageValue"
                label="Offset Package Value (ex. GST)"
                fullWidth
                margin="dense"
                disabled={true}
                value={offsetPackageValue}
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={6}>
            <FormControl fullWidth>
              <DatePicker
                label="Start Date"
                onChange={(value) => {
                  if (value) {
                    formik.setFieldValue("startDate", value, true);
                  }
                }}
                format="DD-MM-YYYY"
                value={formik.values.startDate}
                slotProps={{
                  textField: {
                    helperText:
                      formik.touched.startDate &&
                      Boolean(formik.errors.startDate)
                        ? "Invalid date"
                        : "",
                    error:
                      formik.touched.startDate &&
                      Boolean(formik.errors.startDate),
                  },
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={6}>
            <FormControl fullWidth>
              <DatePicker
                label="End Date"
                onChange={(value) => {
                  if (value) {
                    formik.setFieldValue("endDate", value, true);
                  }
                }}
                format="DD-MM-YYYY"
                value={formik.values.endDate}
                slotProps={{
                  textField: {
                    helperText:
                      formik.touched.endDate && Boolean(formik.errors.startDate)
                        ? "Invalid date"
                        : "",
                    error:
                      formik.touched.endDate && Boolean(formik.errors.endDate),
                  },
                }}
                minDate={dayjs(project.startDate)}
                maxDate={dayjs(project.endDate)}
              />
            </FormControl>
          </Grid>
          <Grid
            item
            xs={6}
            sm={6}
            sx={{
              display: "flex !important",
              justifyContent: "left !important;",
            }}
          ></Grid>
          <Grid
            item
            xs={6}
            sm={6}
            sx={{
              display: "flex !important",
              justifyContent: "right !important;",
            }}
          >
            <StyledButton variant="contained" type="submit">
              Next
            </StyledButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
